import type { RulebookAttachmentSelection } from "@next-platform/client-common-core/rulebook/attach/useRulebookAttachmentSelection";
import React, { memo, useCallback, useState } from "react";

import ModalRulebook from "./ModalRulebook";
import RulebookSection from "./RulebookSection";

type RulebookAttachmentFieldProps = {
  /** From `useRulebookAttachmentSelection` in the owning form (character or persona). */
  selection: RulebookAttachmentSelection;
  disabled?: boolean;
  dataTestId?: string;
};

/**
 * Rulebook section plus its picker for a character or persona form; changes apply when the owner is saved.
 * Renders nothing when the selection is gated off (`rulebook.characterAttach` / `rulebook.personaAttach` + `rulebook.manage`).
 */
const RulebookAttachmentField = ({ selection, disabled = false, dataTestId = "RulebookSection" }: RulebookAttachmentFieldProps) => {
  const [stateIsPickerOpen, setStateIsPickerOpen] = useState(false);
  const { reloadRulebooks } = selection;
  const openPicker = useCallback(() => {
    setStateIsPickerOpen(true);
    void reloadRulebooks();
  }, [reloadRulebooks]);
  const closePicker = useCallback(() => setStateIsPickerOpen(false), []);
  const retry = useCallback(() => {
    void reloadRulebooks();
  }, [reloadRulebooks]);

  if (!selection.isEnabled) {
    return null;
  }

  return (
    <>
      <RulebookSection
        ownerType={selection.ownerType}
        attachedRulebook={selection.currentRulebook}
        isAttachedRulebookMissing={selection.isCurrentRulebookMissing}
        isLoading={selection.isLoading}
        hasLoadError={selection.hasLookupError}
        onRetryLoad={selection.retryLookup}
        onAttach={openPicker}
        onDetach={selection.clear}
        disabled={disabled}
        dataTestId={dataTestId}
      />
      <ModalRulebook
        ownerType={selection.ownerType}
        isOpen={stateIsPickerOpen}
        onClose={closePicker}
        rulebooks={selection.rulebooks}
        isLoading={selection.isListLoading}
        hasError={selection.hasListError}
        onRetry={retry}
        selectedRulebookId={selection.currentRulebookId}
        onRulebookSelected={selection.select}
      />
    </>
  );
};

export default memo(RulebookAttachmentField);
